import React, { useEffect, useMemo, useState } from 'react';
import { FiArrowUp, FiArrowDown, FiArchive } from 'react-icons/fi';
import { useProfiles } from '../../hooks/useProfiles';
import {
    PageHeader,
    EmptyState,
    SkeletonTableRow,
    SearchInput,
} from '../../theme/components';
import { PageButton } from '../../theme/components/PageButton';
import Avatar from '../../theme/components/Avatar';
import { formatDateDisplay } from '../../utils/dates';
import './Profiles.css';

const COLUMNS = [
    { key: 'childNo', label: 'Reg: No', sortable: true, width: '110px' },
    { key: 'name', label: 'Child Name', sortable: true },
    { key: 'dob', label: 'DOB', sortable: true, width: '110px' },
    { key: 'mothersNo', label: "Mother's No", sortable: true, width: '110px' },
    { key: 'gnDevision', label: 'GN Devision', sortable: true, width: '130px' },
];

const ArchivedProfiles = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [sort, setSort] = useState({ key: 'childNo', dir: 'asc' });

    const { data: apiData, loading, error } = useProfiles(undefined, { archived: true });
    const childrenData = useMemo(() => apiData || [], [apiData]);

    // Search stays client-side (typed-as-you-go); the BE only returns
    // archived rows so we don't need to re-filter server-side here.
    const searchedData = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return childrenData;
        return childrenData.filter(
            (c) =>
                c.name?.toLowerCase().includes(q) ||
                c.childNo?.toLowerCase().includes(q) ||
                c.mothersNo?.toLowerCase().includes(q),
        );
    }, [childrenData, searchQuery]);

    const sortedData = useMemo(() => {
        const dir = sort.dir === 'asc' ? 1 : -1;
        const getSortValue = (child, key) => {
            switch (key) {
                case 'name': return child.name || '';
                case 'childNo': return child.childNo || '';
                case 'dob': return child.dob || '';
                case 'mothersNo': return child.mothersNo || '';
                case 'gnDevision': return child.gnDevision || '';
                default: return '';
            }
        };
        return [...searchedData].sort((a, b) => {
            const av = getSortValue(a, sort.key);
            const bv = getSortValue(b, sort.key);
            if (av < bv) return -1 * dir;
            if (av > bv) return 1 * dir;
            return 0;
        });
    }, [searchedData, sort]);

    const toggleSort = (key) => {
        setSort((prev) =>
            prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
        );
    };

    if (loading) {
        return (
            <div className="profiles-wrapper">
                <PageHeader title="Archived Profiles" subtitle="Loading…" icon={FiArchive} />
                <div className="profiles-card">
                    <table className="profiles-table">
                        <thead>
                            <tr>
                                {COLUMNS.map((c) => (
                                    <th key={c.key} style={{ width: c.width }}>{c.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {Array.from({ length: 6 }).map((_, i) => (
                                <SkeletonTableRow key={i} cols={COLUMNS.length} />
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="profiles-wrapper">
                <EmptyState
                    illustration="error"
                    title="Couldn't load archived profiles"
                    description={error.message || String(error)}
                    action={
                        <PageButton type="button" variant="primary" onClick={() => window.location.reload()}>
                            Try again
                        </PageButton>
                    }
                />
            </div>
        );
    }

    return (
        <div className="profiles-wrapper">
            <PageHeader
                title="Archived Profiles"
                subtitle="Children who have aged out (5y+1d) or were manually archived. Read-only — they no longer appear in clinical flows."
                icon={FiArchive}
            />

            <div className="profiles-stat-bar">
                <div className="profiles-stat profiles-stat--primary">
                    <span className="profiles-stat__label">Total</span>
                    <span className="profiles-stat__value">{childrenData.length}</span>
                </div>
            </div>

            <div className="profiles-card">
                <div className="profiles-actions">
                    <SearchInput
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onClear={() => setSearchQuery('')}
                        placeholder="Search by name, reg no, mother's no"
                        ariaLabel="Search archived profiles"
                    />
                </div>

                <div className="table-responsive">
                    <table className="profiles-table">
                        <thead>
                            <tr>
                                {COLUMNS.map((col) => {
                                    const active = sort.key === col.key;
                                    return (
                                        <th
                                            key={col.key}
                                            style={{ width: col.width }}
                                            className={col.sortable ? 'sortable' : ''}
                                            aria-sort={
                                                active
                                                    ? sort.dir === 'asc' ? 'ascending' : 'descending'
                                                    : 'none'
                                            }
                                        >
                                            {col.sortable ? (
                                                <button
                                                    type="button"
                                                    onClick={() => toggleSort(col.key)}
                                                    className="profiles-th-btn"
                                                >
                                                    <span>{col.label}</span>
                                                    {active ? (
                                                        sort.dir === 'asc'
                                                            ? <FiArrowUp size={12} />
                                                            : <FiArrowDown size={12} />
                                                    ) : null}
                                                </button>
                                            ) : (
                                                col.label
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedData.map((child) => (
                                <tr key={child.id} className="profiles-table-row">
                                    <td className="child-no-cell">{child.childNo}</td>
                                    <td className="child-name-cell">
                                        <div className="profiles-name-wrap">
                                            <Avatar
                                                name={child.name}
                                                id={child.id || child.childNo}
                                                size={32}
                                            />
                                            <span>{child.name}</span>
                                        </div>
                                    </td>
                                    <td>{formatDateDisplay(child.dob)}</td>
                                    <td>{child.mothersNo}</td>
                                    <td>{child.gnDevision || '—'}</td>
                                </tr>
                            ))}
                            {sortedData.length === 0 && (
                                <tr>
                                    <td colSpan={COLUMNS.length} className="profiles-empty-cell">
                                        <EmptyState
                                            illustration={searchQuery ? 'search' : 'no-data'}
                                            compact
                                            title={
                                                searchQuery
                                                    ? 'No archived profiles match your search'
                                                    : 'No archived profiles'
                                            }
                                            action={
                                                searchQuery ? (
                                                    <PageButton
                                                        type="button"
                                                        variant="ghost"
                                                        onClick={() => setSearchQuery('')}
                                                    >
                                                        Clear search
                                                    </PageButton>
                                                ) : null
                                            }
                                        />
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ArchivedProfiles;